import React, { useState, useEffect } from 'react';
import { 
  Building2, 
  Smartphone, 
  CreditCard, 
  CheckCircle2, 
  XCircle, 
  RefreshCw, 
  ShieldCheck, 
  Globe2, 
  AlertCircle,
  SlidersHorizontal,
  Lock,
  Cog,
  Key,
  Eye,
  EyeOff,
  Save,
  X
} from 'lucide-react';
import adminApi from '../../api/admin';

const AdminSettingsPage = () => {
  const [activeTab, setActiveTab] = useState('payment');
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});
  const [toast, setToast] = useState(null);

  // Modal State
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [formData, setFormData] = useState({});
  const [showSecrets, setShowSecrets] = useState({});
  const [savingConfig, setSavingConfig] = useState(false);

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const res = await adminApi.getPaymentConfigs();
      if (res.data?.data) {
        setConfigs(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch payment configs', err);
      showToast('Failed to load payment gateway settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleToggleGateway = async (gatewayCode, currentStatus) => {
    const newStatus = !currentStatus;
    setUpdating(prev => ({ ...prev, [gatewayCode]: true }));

    try {
      const res = await adminApi.updatePaymentGatewayConfig(gatewayCode, {
        is_enabled: newStatus
      });
      if (res.data?.data) {
        setConfigs(prev =>
          prev.map(item =>
            item.gateway === gatewayCode ? res.data.data : item
          )
        );
        showToast(
          `${res.data.data.name} is now ${newStatus ? 'ENABLED' : 'DISABLED'}`,
          newStatus ? 'success' : 'warning'
        );
      }
    } catch (err) {
      console.error(`Failed to update ${gatewayCode}`, err);
      showToast(`Failed to update ${gatewayCode} status`, 'error');
    } finally {
      setUpdating(prev => ({ ...prev, [gatewayCode]: false }));
    }
  };

  const openConfigModal = (item) => {
    setSelectedGateway(item);
    setFormData(item.config_data || {});
    setShowSecrets({});
  };

  const closeConfigModal = () => {
    setSelectedGateway(null);
    setFormData({});
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleShowSecret = (field) => {
    setShowSecrets(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSaveCredentials = async (e) => {
    e.preventDefault();
    if (!selectedGateway) return;

    setSavingConfig(true);
    try {
      const res = await adminApi.updatePaymentGatewayConfig(selectedGateway.gateway, {
        config_data: formData
      });

      if (res.data?.data) {
        setConfigs(prev =>
          prev.map(item =>
            item.gateway === selectedGateway.gateway ? res.data.data : item
          )
        );
        showToast(`Saved credentials for ${selectedGateway.name}`, 'success');
        closeConfigModal();
      }
    } catch (err) {
      console.error(`Failed to save config for ${selectedGateway.gateway}`, err);
      showToast(`Failed to save API credentials`, 'error');
    } finally {
      setSavingConfig(false);
    }
  };

  const getGatewayIcon = (code) => {
    switch (code) {
      case 'mpesa':
        return <Smartphone className="w-7 h-7 text-emerald-600" />;
      case 'kcb':
        return <Building2 className="w-7 h-7 text-blue-600" />;
      case 'stripe':
        return <CreditCard className="w-7 h-7 text-purple-600" />;
      default:
        return <CreditCard className="w-7 h-7 text-gray-600" />;
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 p-6 lg:p-10 space-y-8">
      {/* Toast Banner */}
      {toast && (
        <div className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg transition-all animate-bounce ${
          toast.type === 'success' ? 'bg-emerald-600 text-white' :
          toast.type === 'warning' ? 'bg-amber-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <span className="font-medium text-sm">{toast.message}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-200 pb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3 tracking-tight">
            <Cog className="w-8 h-8 text-blue-600" />
            Admin Settings
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Manage system payment gateways, integrations, secret keys, and API credentials
          </p>
        </div>
        <button
          onClick={fetchConfigs}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all shadow-sm"
        >
          <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
          Refresh Settings
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 space-x-6">
        <button
          onClick={() => setActiveTab('payment')}
          className={`flex items-center gap-2 py-3 px-2 text-sm font-semibold border-b-2 transition-all ${
            activeTab === 'payment'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-900'
          }`}
        >
          <SlidersHorizontal className="w-4 h-4" />
          Payment Configurations
        </button>
        <button
          onClick={() => setActiveTab('general')}
          className={`flex items-center gap-2 py-3 px-2 text-sm font-semibold border-b-2 transition-all ${
            activeTab === 'general'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-900'
          }`}
        >
          <ShieldCheck className="w-4 h-4" />
          General & Security
        </button>
      </div>

      {/* Payment Configuration Content */}
      {activeTab === 'payment' && (
        <div className="space-y-6">
          <div className="bg-blue-50/70 border border-blue-200/80 rounded-2xl p-5 flex items-start gap-4 shadow-xs">
            <Globe2 className="w-6 h-6 text-blue-600 shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-950 text-base">
                Payment Gateways Control Center
              </h3>
              <p className="text-sm text-blue-800/90 mt-1 leading-relaxed">
                Enable or disable payment gateways below, and click <strong>"Configure Credentials & Keys"</strong> to manage secret keys, passkeys, base URLs, and API tokens.
              </p>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white rounded-2xl p-6 border border-gray-200 animate-pulse space-y-4 shadow-xs">
                  <div className="h-10 w-10 bg-gray-100 rounded-xl" />
                  <div className="h-6 w-3/4 bg-gray-100 rounded" />
                  <div className="h-12 w-full bg-gray-50 rounded" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {configs.map(item => (
                <div
                  key={item.id || item.gateway}
                  className={`relative bg-white rounded-2xl p-6 border transition-all duration-200 shadow-sm hover:shadow-md flex flex-col justify-between ${
                    item.is_enabled
                      ? 'border-blue-300 ring-1 ring-blue-500/20'
                      : 'border-gray-200 opacity-90'
                  }`}
                >
                  <div className="space-y-4">
                    {/* Icon & Badges */}
                    <div className="flex items-center justify-between">
                      <div className="p-3 bg-gray-50 border border-gray-100 rounded-xl">
                        {getGatewayIcon(item.gateway)}
                      </div>
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                        item.is_enabled
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'bg-gray-100 text-gray-600 border border-gray-200'
                      }`}>
                        {item.is_enabled ? (
                          <>
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Active
                          </>
                        ) : (
                          <>
                            <XCircle className="w-3.5 h-3.5" />
                            Disabled
                          </>
                        )}
                      </span>
                    </div>

                    {/* Title & Description */}
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">
                        {item.name}
                      </h3>
                      <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>

                  {/* Actions & Toggle Switch */}
                  <div className="mt-8 pt-4 border-t border-gray-100 space-y-3">
                    <button
                      type="button"
                      onClick={() => openConfigModal(item)}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-800 rounded-xl text-xs font-semibold transition-colors"
                    >
                      <Key className="w-4 h-4 text-blue-600" />
                      Configure Credentials & Keys
                    </button>

                    <div className="flex items-center justify-between pt-1">
                      <span className="text-sm font-medium text-gray-700">
                        Enable {item.gateway.toUpperCase()}
                      </span>
                      <button
                        type="button"
                        disabled={updating[item.gateway]}
                        onClick={() => handleToggleGateway(item.gateway, item.is_enabled)}
                        className={`relative inline-flex h-7 w-14 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                          item.is_enabled ? 'bg-blue-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            item.is_enabled ? 'translate-x-7' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* General Settings Tab Placeholder */}
      {activeTab === 'general' && (
        <div className="bg-white rounded-2xl p-12 text-center border border-gray-200 space-y-4 shadow-xs">
          <div className="inline-flex p-4 bg-gray-50 border border-gray-200 rounded-full">
            <Lock className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-xl font-bold text-gray-900">Security & General System Settings</h3>
          <p className="text-gray-500 text-sm max-w-md mx-auto">
            API Key management, CORS configuration, and system parameters are managed via server environment variables.
          </p>
        </div>
      )}

      {/* Credentials Configuration Modal */}
      {selectedGateway && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-xs animate-fade-in">
          <div className="bg-white rounded-3xl max-w-2xl w-full border border-gray-200 shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200 flex items-center justify-between bg-gray-50/80">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-blue-50 border border-blue-100 text-blue-600 rounded-xl">
                  {getGatewayIcon(selectedGateway.gateway)}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {selectedGateway.name} Credentials
                  </h3>
                  <p className="text-xs text-gray-500">
                    Configure base URLs, API keys, secret passkeys, and authentication parameters
                  </p>
                </div>
              </div>
              <button
                onClick={closeConfigModal}
                className="p-2 text-gray-400 hover:text-gray-700 rounded-xl hover:bg-gray-200/60 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body Form */}
            <form onSubmit={handleSaveCredentials} className="p-6 space-y-5 overflow-y-auto flex-1 bg-white">
              {/* KCB Fields */}
              {selectedGateway.gateway === 'kcb' && (
                <>
                  <div className="bg-blue-50/70 border border-blue-200 p-4 rounded-2xl text-xs text-blue-900 leading-relaxed space-y-1">
                    <p className="font-semibold text-blue-950 flex items-center gap-1.5">
                      <ShieldCheck className="w-4 h-4 text-blue-600" />
                      KCB Buni Developer Credentials
                    </p>
                    <p>
                      Enter your <strong>Consumer Key</strong> and <strong>Consumer Secret</strong> from your application on the 
                      <a href="https://buni.kcbgroup.com" target="_blank" rel="noreferrer" className="underline font-semibold ml-1">KCB Buni Portal</a>.
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                      Environment Mode
                    </label>
                    <select
                      value={formData.environment || 'sandbox'}
                      onChange={(e) => handleFieldChange('environment', e.target.value)}
                      className="w-full p-3 bg-gray-50 border border-gray-300 rounded-xl text-sm font-semibold text-gray-900 focus:bg-white focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="sandbox">Sandbox / UAT (Testing)</option>
                      <option value="production">Production (Live Payments)</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                        Consumer Key (Buni OAuth2)
                      </label>
                      <input
                        type="text"
                        value={formData.consumer_key || ''}
                        onChange={(e) => handleFieldChange('consumer_key', e.target.value)}
                        placeholder="Consumer Key from Buni Portal"
                        className="w-full p-3 bg-gray-50 border border-gray-300 rounded-xl text-sm font-mono text-gray-900 focus:bg-white focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                        Consumer Secret
                      </label>
                      <div className="relative">
                        <input
                          type={showSecrets['consumer_secret'] ? 'text' : 'password'}
                          value={formData.consumer_secret || ''}
                          onChange={(e) => handleFieldChange('consumer_secret', e.target.value)}
                          placeholder="Consumer Secret from Buni Portal"
                          className="w-full p-3 pr-10 bg-gray-50 border border-gray-300 rounded-xl text-sm font-mono text-gray-900 focus:bg-white focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => toggleShowSecret('consumer_secret')}
                          className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
                        >
                          {showSecrets['consumer_secret'] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                        Org ShortCode / Paybill
                      </label>
                      <input
                        type="text"
                        value={formData.org_short_code || ''}
                        onChange={(e) => handleFieldChange('org_short_code', e.target.value)}
                        placeholder="e.g. 522522 or Partner Code"
                        className="w-full p-3 bg-gray-50 border border-gray-300 rounded-xl text-sm font-mono text-gray-900 focus:bg-white focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                        Org PassKey <span className="text-gray-400 font-normal lowercase">(optional)</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showSecrets['org_pass_key'] ? 'text' : 'password'}
                          value={formData.org_pass_key || ''}
                          onChange={(e) => handleFieldChange('org_pass_key', e.target.value)}
                          placeholder="Optional PassKey if assigned"
                          className="w-full p-3 pr-10 bg-gray-50 border border-gray-300 rounded-xl text-sm font-mono text-gray-900 focus:bg-white focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => toggleShowSecret('org_pass_key')}
                          className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
                        >
                          {showSecrets['org_pass_key'] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* KCB Callback URL Field with Explanatory Card */}
                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      KCB Callback URL (Webhook Endpoint)
                    </label>
                    <input
                      type="url"
                      value={formData.callback_url || ''}
                      onChange={(e) => handleFieldChange('callback_url', e.target.value)}
                      placeholder="https://yourdomain.com/api/v1/subscriptions/webhooks/kcb/"
                      className="w-full p-3 bg-gray-50 border border-gray-300 rounded-xl text-sm font-mono text-gray-900 focus:bg-white focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="p-3 bg-amber-50/80 border border-amber-200/80 rounded-xl text-xs text-amber-900 space-y-1">
                      <p className="font-semibold text-amber-950 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                        How to get & configure this Callback URL:
                      </p>
                      <ul className="list-disc pl-4 space-y-0.5 text-amber-800">
                        <li>
                          <strong>Live / Production:</strong> <code className="bg-amber-100/70 px-1 py-0.5 rounded font-mono text-[11px]">https://yourdomain.com/api/v1/subscriptions/webhooks/kcb/</code>
                        </li>
                        <li>
                          <strong>Local Testing (ngrok):</strong> Launch ngrok (<code className="bg-amber-100/70 px-1 py-0.5 rounded font-mono text-[11px]">ngrok http 8000</code>) and enter your HTTPS URL, e.g. <code className="bg-amber-100/70 px-1 py-0.5 rounded font-mono text-[11px]">https://xxxx.ngrok-free.app/api/v1/subscriptions/webhooks/kcb/</code>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                      API Request Timeout (seconds)
                    </label>
                    <input
                      type="number"
                      value={formData.timeout || 30}
                      onChange={(e) => handleFieldChange('timeout', parseInt(e.target.value) || 30)}
                      placeholder="30"
                      className="w-full p-3 bg-gray-50 border border-gray-300 rounded-xl text-sm font-mono text-gray-900 focus:bg-white focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* KCB Payment Channels Control */}
                  <div className="space-y-3 pt-3 border-t border-gray-200">
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Enabled Payment Channels
                    </label>
                    <p className="text-xs text-gray-500">
                      Select which KCB payment options are visible to customers on checkout.
                    </p>
                    <div className="space-y-2.5 bg-gray-50 p-4 rounded-2xl border border-gray-200">
                      {[
                        { key: 'kcb_account', label: 'KCB Account → KCB', desc: 'Direct transfer from KCB accounts (Default)' },
                        { key: 'mpesa_to_kcb', label: 'M-Pesa → KCB', desc: 'STK push payment routed to KCB Paybill' },
                        { key: 'other_bank_to_kcb', label: 'Other Bank → KCB', desc: 'Interbank / PesaLink transfer instructions' },
                      ].map((ch) => {
                        const enabledChannels = formData.enabled_channels || { kcb_account: true, mpesa_to_kcb: true, other_bank_to_kcb: true };
                        const isChEnabled = enabledChannels[ch.key] !== false;
                        return (
                          <div key={ch.key} className="flex items-center justify-between py-2.5 px-3.5 bg-white rounded-xl border border-gray-200 shadow-2xs">
                            <div>
                              <p className="text-sm font-bold text-gray-800">{ch.label}</p>
                              <p className="text-xs text-gray-500">{ch.desc}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                const updatedChannels = {
                                  ...enabledChannels,
                                  [ch.key]: !isChEnabled
                                };
                                handleFieldChange('enabled_channels', updatedChannels);
                              }}
                              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                isChEnabled ? 'bg-blue-600' : 'bg-gray-200'
                              }`}
                            >
                              <span
                                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                  isChEnabled ? 'translate-x-5' : 'translate-x-0'
                                }`}
                              />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

              {/* M-Pesa Fields */}
              {selectedGateway.gateway === 'mpesa' && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                        Consumer Key
                      </label>
                      <input
                        type="text"
                        value={formData.consumer_key || ''}
                        onChange={(e) => handleFieldChange('consumer_key', e.target.value)}
                        placeholder="Daraja Consumer Key"
                        className="w-full p-3 bg-gray-50 border border-gray-300 rounded-xl text-sm font-mono text-gray-900 focus:bg-white focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                        Consumer Secret
                      </label>
                      <div className="relative">
                        <input
                          type={showSecrets['consumer_secret'] ? 'text' : 'password'}
                          value={formData.consumer_secret || ''}
                          onChange={(e) => handleFieldChange('consumer_secret', e.target.value)}
                          placeholder="Daraja Consumer Secret"
                          className="w-full p-3 pr-10 bg-gray-50 border border-gray-300 rounded-xl text-sm font-mono text-gray-900 focus:bg-white focus:ring-2 focus:ring-emerald-500"
                        />
                        <button
                          type="button"
                          onClick={() => toggleShowSecret('consumer_secret')}
                          className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
                        >
                          {showSecrets['consumer_secret'] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                        Business ShortCode / Paybill
                      </label>
                      <input
                        type="text"
                        value={formData.shortcode || ''}
                        onChange={(e) => handleFieldChange('shortcode', e.target.value)}
                        placeholder="174379"
                        className="w-full p-3 bg-gray-50 border border-gray-300 rounded-xl text-sm font-mono text-gray-900 focus:bg-white focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                        Online Passkey
                      </label>
                      <div className="relative">
                        <input
                          type={showSecrets['passkey'] ? 'text' : 'password'}
                          value={formData.passkey || ''}
                          onChange={(e) => handleFieldChange('passkey', e.target.value)}
                          placeholder="bfb279f9aa9bdbcf..."
                          className="w-full p-3 pr-10 bg-gray-50 border border-gray-300 rounded-xl text-sm font-mono text-gray-900 focus:bg-white focus:ring-2 focus:ring-emerald-500"
                        />
                        <button
                          type="button"
                          onClick={() => toggleShowSecret('passkey')}
                          className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
                        >
                          {showSecrets['passkey'] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                      Callback URL
                    </label>
                    <input
                      type="url"
                      value={formData.callback_url || ''}
                      onChange={(e) => handleFieldChange('callback_url', e.target.value)}
                      placeholder="https://api.winguport.com/api/v1/subscriptions/webhooks/daraja/"
                      className="w-full p-3 bg-gray-50 border border-gray-300 rounded-xl text-sm font-mono text-gray-900 focus:bg-white focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </>
              )}

              {/* Stripe Fields */}
              {selectedGateway.gateway === 'stripe' && (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                      Publishable Key
                    </label>
                    <input
                      type="text"
                      value={formData.publishable_key || ''}
                      onChange={(e) => handleFieldChange('publishable_key', e.target.value)}
                      placeholder="pk_test_..."
                      className="w-full p-3 bg-gray-50 border border-gray-300 rounded-xl text-sm font-mono text-gray-900 focus:bg-white focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                      Secret Key
                    </label>
                    <div className="relative">
                      <input
                        type={showSecrets['secret_key'] ? 'text' : 'password'}
                        value={formData.secret_key || ''}
                        onChange={(e) => handleFieldChange('secret_key', e.target.value)}
                        placeholder="sk_test_..."
                        className="w-full p-3 pr-10 bg-gray-50 border border-gray-300 rounded-xl text-sm font-mono text-gray-900 focus:bg-white focus:ring-2 focus:ring-purple-500"
                      />
                      <button
                        type="button"
                        onClick={() => toggleShowSecret('secret_key')}
                        className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
                      >
                        {showSecrets['secret_key'] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                      Webhook Signing Secret
                    </label>
                    <div className="relative">
                      <input
                        type={showSecrets['webhook_secret'] ? 'text' : 'password'}
                        value={formData.webhook_secret || ''}
                        onChange={(e) => handleFieldChange('webhook_secret', e.target.value)}
                        placeholder="whsec_..."
                        className="w-full p-3 pr-10 bg-gray-50 border border-gray-300 rounded-xl text-sm font-mono text-gray-900 focus:bg-white focus:ring-2 focus:ring-purple-500"
                      />
                      <button
                        type="button"
                        onClick={() => toggleShowSecret('webhook_secret')}
                        className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
                      >
                        {showSecrets['webhook_secret'] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Modal Footer */}
              <div className="pt-4 border-t border-gray-200 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={closeConfigModal}
                  className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingConfig}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold transition-colors shadow-md disabled:opacity-50"
                >
                  <Save className={`w-4 h-4 ${savingConfig ? 'animate-spin' : ''}`} />
                  {savingConfig ? 'Saving...' : 'Save Configuration'}
                </button>
              </div>
            </form>

          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSettingsPage;
